import React, { useEffect } from 'react';
import { observer } from 'mobx-react';

import { ScrollView, TouchableOpacity } from 'react-native';
import { useNetInfo } from '@react-native-community/netinfo';
import { useQuery } from '@apollo/client';

import Constants from 'expo-constants';
import Error from '../../../components/Error/Error';
import Loading from '../../../components/Loading/Loading';
import RenderHtml from 'react-native-render-html';
import { COLORS } from '../../../config/designSystem/Colors';
import { Feather } from '@expo/vector-icons';
import {RETRIEVE_STATIC_PAGE} from '../../../state/queries/StaticPage';
import { SPACING } from '../../../config/designSystem/Spacing';
import { SettingStackParamList } from '../../../types/navigation';
import { privateKey } from '../../../constants/routeKeys';
import { router, useLocalSearchParams, useNavigation, useSegments } from 'expo-router';
import { useAvailableDimensions } from '../../../hooks/ui/useAvailableDimensions';
import { useStores } from '../../../hooks/useStores';



type StaticPageProps = SettingStackParamList['StaticPage'];


const StaticPage: React.FunctionComponent<StaticPageProps> = observer(() => {

  const params = useLocalSearchParams();
  const name = params?.name;

  const rootStore = useStores();
  const netInfo = useNetInfo();
  const navigation = useNavigation();
  const segments = useSegments();

  const [content, contentChange] = React.useState('');
  const [connected, setConnected] = React.useState(true);
  const {width} = useAvailableDimensions();


  const systemFonts = [
    'QuattrocentoSans_400Regular',
    'QuattrocentoSans_700Bold',
    'PublicSansBold',
    'PublicSansRegular',
    ...Constants.systemFonts
  ];

  const WebDisplay = (source: any, width: number) => {
    return (
      <RenderHtml
        contentWidth={width}
        source={source}
        systemFonts={systemFonts}
      />
    );
  };

  const { loading, error } = useQuery(RETRIEVE_STATIC_PAGE,
    {
      variables: {
        name
      },
      onCompleted: data => {
        if (data.getByPage.content) {
          contentChange(data.getByPage.content);
        } else {
          contentChange(rootStore?.language.translate.MOBILE.SETTINGS.STATIC_PAGE.NOT_AVAILABLE_IN_THIS_LANGUAGE);
        }
      },
      onError: () => {
        setConnected(netInfo.isConnected !== false && netInfo.isInternetReachable !== false);
      }
    });

  const isPrivate = segments.includes(privateKey);

  useEffect(() => {
    if(name) {
      const accessor = (name as string).replace(/-/g, '_').toUpperCase();
      navigation.setOptions({ headerTitle: rootStore?.language.translate.MOBILE.SETTINGS?.STATIC_PAGE[accessor] });
      // The header used to be turned on only inside the private group, so reaching this
      // page from Register -> "terms and conditions" rendered no header at all and left
      // the user with no way back. It is the FIRST screen of the public (Setting) stack,
      // so there is no automatic back button to inherit either -- hence the explicit
      // headerLeft below. router.back() returns to whichever screen pushed us here.
      navigation.setOptions({
        headerShown: true,
        ...(isPrivate ? {} : {
          headerLeft: () => (
            <TouchableOpacity
              onPress={() => router.back()}
              accessibilityRole="button"
              accessibilityLabel="Back"
              hitSlop={{top: 12, bottom: 12, left: 12, right: 12}}
              style={{paddingHorizontal: SPACING['sp-8']}}
            >
              <Feather name="chevron-left" size={24} color={COLORS.border.accent} />
            </TouchableOpacity>
          ),
        }),
      });
    }
    return () => {
      if(isPrivate) {
        navigation.setOptions({headerShown: false});
      }
    };
  }, [name, segments, isPrivate]);

  if (loading || !content || content.length === 0) {
    return (
      <Loading withLabel={false} />
    );
  }
  if (error) {
    return (
      <Error network={!connected} labels={rootStore?.language.translate.MOBILE?.ERROR} />
    );
  }
  return (
    <ScrollView
      contentContainerStyle={{ justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: SPACING['sp-32'],
      }}
    >
      {WebDisplay({ html: content },width )}
    </ScrollView>

  );

}
);

export default StaticPage;
